/* eslint-disable @typescript-eslint/no-unused-vars, jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import logoImage from '../tbl_logo.png';
// @ts-ignore
import productLogoNew from '../product_logo_new.png';

const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script ${src}`));
        document.head.appendChild(script);
    });
};

const CSRCalculator: React.FC = () => {
    const navigate = useNavigate();

    const financialYears: string[] = [];
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentFyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
    const currentFyStr = `FY ${currentFyStartYear}-${(currentFyStartYear + 1).toString().slice(-2)}`;

    // Generate years from 2021 up to 2050
    for (let year = 2021; year <= 2050; year++) {
        financialYears.push(`FY ${year}-${(year + 1).toString().slice(-2)}`);
    }

    const [calc, setCalc] = useState({
        company_name: '',
        contact_email: '',
        financial_year: currentFyStr,
        net_worth_crore: '',
        turnover_crore: '',
        net_profit_y1_crore: '',
    });
    const [showResult, setShowResult] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [eligibilityCheck, setEligibilityCheck] = useState({
        netWorth: '',
        turnover: '',
        profit: ''
    });
    const [eligibilityMessage, setEligibilityMessage] = useState('');

    const handleEligibilityChange = (key: 'netWorth' | 'turnover' | 'profit', value: 'yes' | 'no') => {
        setEligibilityCheck(prev => ({ ...prev, [key]: value }));
        setEligibilityMessage('');
    };

    const isEligibleFromCheck = eligibilityCheck.netWorth === 'yes' || eligibilityCheck.turnover === 'yes' || eligibilityCheck.profit === 'yes';

    const handleCheckEligibility = () => {
        if (!eligibilityCheck.netWorth || !eligibilityCheck.turnover || !eligibilityCheck.profit) {
            setEligibilityMessage('Please answer all three questions.');
            return;
        }
        if (isEligibleFromCheck) {
            setEligibilityMessage('You are eligible for CSR. Please proceed to calculate your obligation.');
        } else {
            setEligibilityMessage('Based on your answers, you are likely not eligible for CSR.');
        }
    };

    const handleCalcChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setCalc({ ...calc, [e.target.name]: e.target.value });
    };

    const parseNum = (v: string) => {
        const n = parseFloat(v);
        return isNaN(n) ? 0 : n;
    };

    const netWorth = parseNum(calc.net_worth_crore);
    const turnover = parseNum(calc.turnover_crore);
    const lastYearProfit = parseNum(calc.net_profit_y1_crore);
    const cNetWorth = netWorth >= 500;
    const cTurnover = turnover >= 1000;
    const cProfit = lastYearProfit >= 5;
    const isApplicable = cNetWorth || cTurnover || cProfit;
    const avgProfit = parseNum(calc.net_profit_y1_crore);
    const obligation = isApplicable ? +(avgProfit * 0.02).toFixed(2) : 0;
    const criteriaMet = [
        cNetWorth ? 'Net Worth >= Rs. 500 Cr' : null,
        cTurnover ? 'Turnover >= Rs. 1000 Cr' : null,
        cProfit ? 'Net Profit (preceding year) >= Rs. 5 Cr' : null,
    ].filter(Boolean) as string[];

    const handleCalcSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setShowResult(false);
        if (calc.net_profit_y1_crore.trim() === '') {
            return;
        }
        setShowResult(true);
    };

    const handleDownloadPDF = async () => {
        setIsPdfLoading(true);
        try {
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
            const { jsPDF } = (window as any).jspdf;
            if (!jsPDF) {
                console.error('jsPDF not found on window object.');
                setIsPdfLoading(false);
                return;
            }

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageCenter = pageWidth / 2;
            const margin = 20;
            let y = 15;

            const primaryColor = '#17643a';
            const textColor = '#1b3a2b';
            const mutedColor = '#6b9080';
            const accentColor = '#ef4444';

            // Top Header Line
            doc.setDrawColor(primaryColor);
            doc.setLineWidth(1);
            doc.line(margin, y, pageWidth - margin, y);
            y += 15;

            // Watermark (Triple Image + Text)
            const ph = doc.internal.pageSize.getHeight();
            const pw = doc.internal.pageSize.getWidth();
            
            try {
                const wImg = new Image();
                wImg.src = productLogoNew;
                await new Promise((resolve) => {
                    wImg.onload = resolve;
                    wImg.onerror = resolve;
                });

                if (wImg.naturalWidth > 0) {
                    const drawSingleWatermark = (yCenter: number) => {
                        doc.saveGraphicsState();
                        doc.setGState(new doc.GState({ opacity: 0.05 }));
                        
                        const ar = wImg.naturalWidth / wImg.naturalHeight;
                        const wH = 22; 
                        const wW = wH * ar;
                        
                        // Centered Logo
                        doc.addImage(wImg, 'PNG', (pw - wW) / 2, yCenter - (wH / 2), wW, wH, undefined, 'FAST', -45);
                        
                        doc.restoreGraphicsState();
                    };

                    drawSingleWatermark(ph * 0.25);
                    drawSingleWatermark(ph * 0.50);
                    drawSingleWatermark(ph * 0.75);
                }
            } catch (e) {
                console.warn('Advanced watermark failed:', e);
            }

            // Logo Branding
            try {
                const img = document.querySelector('.logo img') as HTMLImageElement;
                if (img) {
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    const logoHeight = 10;
                    const logoWidth = logoHeight * aspectRatio;
                    doc.addImage(img, 'PNG', pageWidth - margin - logoWidth, y - 5, logoWidth, logoHeight);
                }
            } catch (e) {
                console.warn('Could not add logo to PDF:', e);
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(22);
            doc.setTextColor(primaryColor);
            doc.text('CSR Applicability Report', margin, y);
            y += 8;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(mutedColor);
            doc.text('An Initiative by TBL Impact Anchor', margin, y);
            y += 15;

            doc.setDrawColor('#e2e8f0');
            doc.setLineWidth(0.2);
            doc.line(margin, y, pageWidth - margin, y);
            y += 15;

            const drawSectionTitle = (title: string) => {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(14);
                doc.setTextColor(primaryColor);
                doc.text(title, margin, y);
                y += 8;
            };

            const drawDataItem = (label: string, value: string) => {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(textColor);
                doc.text(`${label}:`, margin, y);
                doc.setFont('helvetica', 'normal');
                doc.text(value, margin + 55, y);
                y += 7;
            };

            drawSectionTitle('Company Details');
            drawDataItem('Company Name', calc.company_name || 'N/A');
            drawDataItem('Financial Year', calc.financial_year || 'N/A');
            y += 10;

            drawSectionTitle('Financial Inputs (in Rs. Cr)');
            drawDataItem('Net Worth', netWorth.toFixed(2));
            drawDataItem('Turnover', turnover.toFixed(2));
            drawDataItem('Net Profit (Preceding FY)', avgProfit.toFixed(2));
            y += 10;

            drawSectionTitle('Assessment Result');
            if (isApplicable) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor('#16a34a');
                doc.text('CSR IS APPLICABLE', margin, y);
                y += 8;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(11);
                doc.setTextColor(mutedColor);
                doc.text(`Based on: ${criteriaMet.join(', ')}`, margin, y);
                y += 13;

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.setTextColor(textColor);
                doc.text('Estimated CSR Obligation:', margin, y);
                y += 10;

                doc.setFontSize(18);
                doc.setTextColor(primaryColor);
                doc.text(`Rs. ${obligation.toFixed(2)} Cr`, margin, y);
            } else {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(16);
                doc.setTextColor('#ef4444');
                doc.text('CSR IS NOT APPLICABLE', margin, y);
            }

            y = 250;
            doc.setFontSize(10);
            doc.setTextColor('#ef4444');
            doc.setFont('helvetica', 'bold');
            doc.text("MANADATORY DISCLAIMER:", margin, y);
            y += 5;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            const disclaimerLine1 = "This calculation is based on the Section 135 of Indian Companies Act, 2013 and the numbers provided by the user.";
            const disclaimerLine2 = "Please verify with your Chartered Accountant (CA) or Legal counsel for the actual eligibility criteria or statutory obligation.";
            doc.text(disclaimerLine1, margin, y);
            y += 5;
            doc.text(disclaimerLine2, margin, y);

            doc.setDrawColor('#e2e8f0');
            doc.line(margin, 280, pageWidth - margin, 280);
            doc.setFontSize(8);
            doc.setTextColor(mutedColor);
            doc.text(`Report generated by TBL Impact Anchor on: ${new Date().toLocaleString()}`, pageCenter, 285, { align: 'center' });

            doc.save(`CSR_Report_${calc.company_name || 'Analysis'}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
        setIsPdfLoading(false);
    };

    const EligibilityQuestion: React.FC<{
        question: string;
        field: 'netWorth' | 'turnover' | 'profit';
    }> = ({ question, field }) => (
        <div className="eligibility-question">
            <label>{question}</label>
            <div className="toggle-group">
                <button
                    type="button"
                    className={`btn-toggle ${eligibilityCheck[field] === 'yes' ? 'active' : ''}`}
                    onClick={() => handleEligibilityChange(field, 'yes')}
                >
                    Yes
                </button>
                <button
                    type="button"
                    className={`btn-toggle ${eligibilityCheck[field] === 'no' ? 'active' : ''}`}
                    onClick={() => handleEligibilityChange(field, 'no')}
                >
                    No
                </button>
            </div>
        </div>
    );

    return (
        <div className="home-page calculator-page" style={{ background: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <header className="header">
                <div className="header-left">
                    <a
                        href="https://www.tblservices.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="logo"
                        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', position: 'relative' }}
                    >
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <img src={logoImage} alt="TBL Impact Anchor" style={{ height: '45px', objectFit: 'contain' }} />
                            <span style={{
                                position: 'absolute',
                                top: '-2px',
                                right: '2px',
                                fontSize: '0.45rem',
                                color: '#000',
                                fontWeight: '900',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                pointerEvents: 'none'
                            }}>BETA</span>
                        </div>
                    </a>
                    <nav className="header-nav-tabs">
                        <a onClick={() => navigate('/')} className="nav-link" style={{ cursor: 'pointer' }}>Home</a>
                        <a onClick={() => navigate('/why')} className="nav-link" style={{ cursor: 'pointer' }}>Why</a>
                        <a onClick={() => navigate('/what')} className="nav-link" style={{ cursor: 'pointer' }}>What</a>
                        <a onClick={() => navigate('/how')} className="nav-link" style={{ cursor: 'pointer' }}>How</a>
                    </nav>
                </div>

                <div className="header-right">
                    <div className="social-links">
                        <a href="https://www.linkedin.com/company/tblconsultancy/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                        </a>
                        <a href="https://www.facebook.com/tblconsultancy/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                        </a>
                        <a href="https://www.instagram.com/tbl_consultancy_services" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                        </a>
                        <a href="https://www.youtube.com/@tblservices" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2C5.12 20 12 20 12 20s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 2.78 2.78 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.45 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
                        </a>
                    </div>
                    <button className="btn btn-contact-us" onClick={() => navigate('/contact-us')}>
                        CONTACT US
                    </button>
                    <button className="btn btn-login-compact" onClick={() => navigate('/login')}>
                        Login
                    </button>
                </div>
            </header>

            <main style={{ padding: 0, width: '100%', maxWidth: 'none' }}>
                <section className="calculator-hero" style={{ 
                    background: 'linear-gradient(135deg, #e0f2fe 0%, #ecfdf3 40%, #ffffff 100%)',
                    padding: '5rem 1.25rem',
                    textAlign: 'center',
                    borderBottom: '1px solid rgba(16, 185, 129, 0.1)'
                }}>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        <h1 style={{ color: '#022c22', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1.5rem' }}>
                            CSR Applicability <span style={{ color: '#10b981' }}>Calculator</span>
                        </h1>
                        <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.6, maxWidth: '600px', margin: '0 auto' }}>
                            Accurately determine your company's CSR obligations under Section 135 of the Indian Companies Act, 2013 with our professional assessment tool.
                        </p>
                    </div>
                </section>

                <section style={{ padding: '4rem 1.25rem', background: '#f5f1f0' }}>
                    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div className="calculator-wrapper" style={{ 
                            background: '#ffffff', 
                            padding: '3.5rem', 
                            borderRadius: '32px', 
                            boxShadow: '0 25px 50px -12px rgba(2, 44, 34, 0.08)',
                            border: '1px solid rgba(16, 185, 129, 0.1)',
                            maxWidth: '1000px',
                            margin: '0 auto'
                        }}>
                            <div className="calculator-form">
                                {step === 1 && (
                                    <div className="eligibility-checker">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
                                            <div style={{ background: '#10b981', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>1</div>
                                            <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem' }}>Eligibility Check</h4>
                                        </div>
                                        <p className="eligibility-description" style={{ marginBottom: '2.5rem', color: '#475569', fontSize: '1.05rem' }}>
                                            To begin, please confirm if any of the following statutory thresholds were met in the <strong>preceding financial year</strong>:
                                        </p>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                            <EligibilityQuestion question="Net worth of ₹500 crore or more?" field="netWorth" />
                                            <EligibilityQuestion question="Turnover of ₹1,000 crore or more?" field="turnover" />
                                            <EligibilityQuestion question="Net profit of ₹5 crore or more?" field="profit" />
                                        </div>

                                        {eligibilityMessage && (
                                            <div style={{ 
                                                marginTop: '2rem', 
                                                padding: '1.25rem', 
                                                borderRadius: '16px', 
                                                background: isEligibleFromCheck ? '#f0fdf4' : '#fef2f2', 
                                                border: `1px solid ${isEligibleFromCheck ? '#dcfce7' : '#fee2e2'}`,
                                                color: isEligibleFromCheck ? '#166534' : '#991b1b',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.75rem'
                                            }}>
                                                <span>{isEligibleFromCheck ? '✅' : 'ℹ️'}</span>
                                                <p style={{ margin: 0, fontWeight: 500 }}>{eligibilityMessage}</p>
                                            </div>
                                        )}

                                        {!isEligibleFromCheck && (
                                            <button type="button" className="btn btn-primary" onClick={handleCheckEligibility} style={{ marginTop: '2.5rem', width: '100%', padding: '1rem', fontSize: '1rem' }}>
                                                Analyze Eligibility
                                            </button>
                                        )}

                                        {isEligibleFromCheck && (
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setStep(2);
                                                    setEligibilityMessage('');
                                                }}
                                                style={{ marginTop: '2.5rem', width: '100%', padding: '1rem', fontSize: '1rem' }}
                                            >
                                                Proceed to Calculation →
                                            </button>
                                        )}
                                    </div>
                                )}

                                {step === 2 && (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2.5rem' }}>
                                            <div style={{ background: '#10b981', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>2</div>
                                            <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem' }}>Financial Data Entry</h4>
                                        </div>
                                        <form onSubmit={handleCalcSubmit}>
                                            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                                <div className="form-group"><label>Company Name <span className="required-mark">*</span></label><input type="text" name="company_name" value={calc.company_name} onChange={handleCalcChange} required /></div>
                                                <div className="form-group"><label>Contact Email <span className="required-mark">*</span></label><input type="email" name="contact_email" value={calc.contact_email} onChange={handleCalcChange} required /></div>
                                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                                    <label>Financial Year Assessment <span className="required-mark">*</span></label>
                                                    <select name="financial_year" value={calc.financial_year} onChange={handleCalcChange} required>
                                                        {financialYears.map(fy => (
                                                            <option key={fy} value={fy}>{fy}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group"><label>Net Worth (In Cr) <span className="required-mark">*</span></label><input type="text" name="net_worth_crore" value={calc.net_worth_crore} onChange={handleCalcChange} required /></div>
                                                <div className="form-group"><label>Turnover (In Cr) <span className="required-mark">*</span></label><input type="text" name="turnover_crore" value={calc.turnover_crore} onChange={handleCalcChange} required /></div>
                                                <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Net Profit (In Cr) <span className="required-mark">*</span></label><input type="text" name="net_profit_y1_crore" value={calc.net_profit_y1_crore} onChange={handleCalcChange} required /></div>
                                            </div>
                                            <button type="submit" className="btn btn-primary" style={{ marginTop: '2.5rem', width: '100%', padding: '1rem' }}>
                                                Calculate CSR Obligation
                                            </button>
                                        </form>
                                    </>
                                )}

                                {showResult && step === 2 && (
                                    <div className={`result-card ${isApplicable ? 'applicable' : 'not-applicable'}`} style={{ 
                                        marginTop: '3rem', 
                                        padding: '3rem', 
                                        borderRadius: '24px', 
                                        background: isApplicable ? '#f0fdf4' : '#fef2f2', 
                                        border: `1px solid ${isApplicable ? '#dcfce7' : '#fee2e2'}`,
                                        textAlign: 'center'
                                    }}>
                                        <h3 style={{ color: isApplicable ? '#166534' : '#991b1b', fontSize: '1.75rem', marginBottom: '1rem' }}>
                                            {isApplicable ? "✓ CSR is Applicable" : "✕ CSR is Not Applicable"}
                                        </h3>
                                        {isApplicable && (
                                            <>
                                                <p style={{ color: '#4d7c0f', marginBottom: '2rem', fontWeight: 500 }}>
                                                    Condition Met: {criteriaMet.join(' & ')}
                                                </p>
                                                <div style={{ background: '#fff', padding: '2rem', borderRadius: '16px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', display: 'inline-block', minWidth: '300px' }}>
                                                    <p style={{ color: '#64748b', marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Total CSR Obligation</p>
                                                    <p style={{ fontSize: '3.5rem', fontWeight: '900', color: '#047857', margin: 0 }}>₹ {obligation.toFixed(2)} <span style={{ fontSize: '1.5rem' }}>Cr</span></p>
                                                </div>
                                                <div style={{ marginTop: '2.5rem' }}>
                                                    <button
                                                        onClick={handleDownloadPDF}
                                                        className="btn btn-accent"
                                                        disabled={isPdfLoading}
                                                        style={{ padding: '1rem 2.5rem', background: '#047857', color: '#fff', border: 'none', borderRadius: '999px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(4, 120, 87, 0.2)' }}
                                                    >
                                                        {isPdfLoading ? 'Generating Analysis...' : 'Download Full Report (PDF)'}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {showResult && step === 2 && (
                                    <div style={{ marginTop: '3rem', padding: '1.5rem', borderLeft: '4px solid #ef4444', background: '#fef2f2', borderRadius: '0 12px 12px 0' }}>
                                        <p style={{ color: '#991b1b', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>
                                            <strong>LEGAL DISCLAIMER:</strong> This calculation is for informational purposes only and is based on Section 135 of the Indian Companies Act, 2013. 
                                            We strongly recommend validating these findings with your Chartered Accountant or legal counsel before making statutory filings.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default CSRCalculator;
